import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import type { MessageSourceType } from '../analytics/types';
import { getSearchBarPlaceholder } from '../config/constants';
import type { CTA } from '../config/types';
import { useChatLanguage, useChatLocale } from '../context/ChatThemeContext';
import type { DynamicQuestionItem } from '../utils/content/dynamicQuestions';
import GlassContainer from './GlassContainer';
import ChatDisclaimer from './searchbar/ChatDisclaimer';
import DynamicQuestionsStrip from './searchbar/DynamicQuestionsStrip';
import SearchBarCTAGroup from './searchbar/SearchBarCTAGroup';
import SearchBarInputField from './searchbar/SearchBarInputField';

interface SearchBarProps {
    onSendMessage: (messageToSend: string, messageSource?: MessageSourceType) => void;
    isLoading: boolean;
    primaryColor: string;
    placeholder?: string;
    showPlaceholder?: boolean;
    isExpanded?: boolean;
    siteConfig?: { name: string };
    domain: string;
    dynamicQuestions?: DynamicQuestionItem[];
    showDynamicQuestions?: boolean;
    dynamicQuestionAnimationKey?: number;
    areDynamicQuestionsCollapsed?: boolean;
    onDynamicQuestionsCollapseToggle?: () => void;
    onDynamicQuestionClick?: (question: string) => void;
    ctas?: CTA[];
    showCtas?: boolean;
    ctaAnimationKey?: number;
    showShimmer?: boolean;
    showDisclaimer?: boolean;
    compactDisclaimer?: boolean;
    onUserTypingChange?: (isTyping: boolean) => void;
    autoFocus?: boolean;
    onBlurWithoutText?: () => void;
    onEngage?: () => void;
    showShadow?: boolean;
    showBorder?: boolean;
    isCopilotLayout?: boolean;
    isCtaPage?: boolean;
    hadDemoOffer?: boolean;
    inlineCtaOptionEnabled?: boolean;
    visitorEmail?: string | null;
    onBookingFlowStart?: (
        ctaId: string,
        redirectUrl: string,
        bookingMessage: string,
        ctaAction?: 'navigate' | 'popup' | 'in-chat'
    ) => void;
    onMinimize?: () => void;
    forceHideCTAs?: boolean;
    'data-ph-capture-attribute-input-area'?: string;
}

const SearchBar: React.FC<SearchBarProps> = ({
    onSendMessage,
    isLoading,
    primaryColor,
    placeholder,
    showPlaceholder = true,
    isExpanded = false,
    siteConfig,
    domain,
    dynamicQuestions,
    showDynamicQuestions = false,
    dynamicQuestionAnimationKey,
    areDynamicQuestionsCollapsed = false,
    onDynamicQuestionsCollapseToggle,
    onDynamicQuestionClick,
    ctas,
    showCtas = false,
    ctaAnimationKey,
    showShimmer = false,
    showDisclaimer = false,
    compactDisclaimer = false,
    onUserTypingChange,
    autoFocus = false,
    showShadow = false,
    showBorder = true,
    isCopilotLayout = false,
    isCtaPage,
    hadDemoOffer,
    inlineCtaOptionEnabled,
    visitorEmail,
    onBookingFlowStart,
    onMinimize,
    forceHideCTAs = false,
    'data-ph-capture-attribute-input-area': inputAreaAttribute,
    onBlurWithoutText,
    onEngage,
}) => {
    const [message, setMessage] = useState('');
    const wasTypingRef = useRef(false);

    useEffect(() => {
        if (onUserTypingChange) {
            const isTyping = message.trim().length > 0;
            if (isTyping !== wasTypingRef.current) {
                wasTypingRef.current = isTyping;
                onUserTypingChange(isTyping);
            }
        }
    }, [message, onUserTypingChange]);

    const effectiveLanguage = useChatLanguage();
    const effectiveLocale = useChatLocale();

    const placeholderType = isExpanded
        ? message.length > 0
            ? 'EXPANDED_EXISTING_CONVERSATION'
            : 'EXPANDED_NEW_CONVERSATION'
        : 'COLLAPSED';

    const localizedPlaceholder = getSearchBarPlaceholder(
        placeholderType,
        effectiveLanguage,
        siteConfig?.name
    );
    const finalPlaceholder = placeholder ?? localizedPlaceholder ?? 'Ask us anything 👋';

    const [hasShownCtasOnce, setHasShownCtasOnce] = useState(false);
    const [hasShownDynamicQuestionsOnce, setHasShownDynamicQuestionsOnce] = useState(false);

    useEffect(() => {
        if (showCtas && ctas && ctas.length > 0) {
            setHasShownCtasOnce(true);
        }
    }, [showCtas, ctas]);

    useEffect(() => {
        if (showDynamicQuestions && dynamicQuestions && dynamicQuestions.length > 0) {
            setHasShownDynamicQuestionsOnce(true);
        }
    }, [showDynamicQuestions, dynamicQuestions]);

    const shouldRenderDynamicQuestionsStrip =
        Boolean(dynamicQuestions && dynamicQuestions.length > 0) &&
        (showDynamicQuestions || hasShownDynamicQuestionsOnce);

    const shouldRenderCtaGroup =
        !shouldRenderDynamicQuestionsStrip &&
        Boolean(ctas && ctas.length > 0) &&
        (showCtas || hasShownCtasOnce) &&
        !forceHideCTAs;

    const shouldShowActionsGap =
        (shouldRenderDynamicQuestionsStrip &&
            showDynamicQuestions &&
            !areDynamicQuestionsCollapsed) ||
        (shouldRenderCtaGroup && showCtas);

    return (
        <GlassContainer
            className="rose-search-bar-glass-container relative w-full"
            borderRadius={
                showDisclaimer
                    ? 'var(--rw-radius-shell-compact) var(--rw-radius-shell-compact) 0 0'
                    : 'var(--rw-radius-shell-compact)'
            }
            padding="0"
            showShadow={showShadow}
            showBorder={showBorder}
            data-ph-capture-attribute-input-area={inputAreaAttribute}
        >
            <div
                className={`rose-search-bar-main-content flex flex-col p-2 ${shouldShowActionsGap ? 'gap-[10px]' : 'gap-0'}`}
            >
                {shouldRenderDynamicQuestionsStrip && (
                    <DynamicQuestionsStrip
                        questions={dynamicQuestions ?? []}
                        showDynamicQuestions={showDynamicQuestions}
                        hasShownDynamicQuestionsOnce={hasShownDynamicQuestionsOnce}
                        areDynamicQuestionsCollapsed={areDynamicQuestionsCollapsed}
                        onDynamicQuestionsCollapseToggle={onDynamicQuestionsCollapseToggle}
                        onDynamicQuestionClick={onDynamicQuestionClick}
                        dynamicQuestionAnimationKey={dynamicQuestionAnimationKey}
                        effectiveLocale={effectiveLocale}
                    />
                )}

                {shouldRenderCtaGroup && (
                    <SearchBarCTAGroup
                        ctas={ctas ?? []}
                        showCtas={showCtas}
                        domain={domain}
                        ctaAnimationKey={ctaAnimationKey}
                        isCtaPage={isCtaPage}
                        hadDemoOffer={hadDemoOffer}
                        inlineCtaOptionEnabled={inlineCtaOptionEnabled}
                        visitorEmail={visitorEmail}
                        onBookingFlowStart={onBookingFlowStart}
                        onMinimize={onMinimize}
                        isCopilotLayout={isCopilotLayout}
                    />
                )}

                <SearchBarInputField
                    message={message}
                    onMessageChange={setMessage}
                    onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey && message.trim()) {
                            e.preventDefault();
                            if (isLoading) return;
                            onSendMessage(message);
                            setMessage('');
                        }
                    }}
                    onSendMessage={(messageSource) => {
                        if (isLoading) return;
                        if (message.trim()) {
                            onEngage?.();
                            onSendMessage(message, messageSource);
                            setMessage('');
                        }
                    }}
                    isLoading={isLoading}
                    primaryColor={primaryColor}
                    showPlaceholder={showPlaceholder}
                    placeholder={finalPlaceholder}
                    showShimmer={showShimmer}
                    autoFocus={autoFocus}
                    onBlurWithoutText={onBlurWithoutText}
                    onEngage={onEngage}
                />
            </div>

            {showDisclaimer && (
                <div
                    className={`rose-search-bar-disclaimer-area px-2 ${isExpanded ? 'pb-2' : 'pb-2'}`}
                >
                    <ChatDisclaimer compact={compactDisclaimer} />
                </div>
            )}
        </GlassContainer>
    );
};

export default SearchBar;
